<template>
  <div class="motd-overlay" @click.self="$emit('close')">
    <div class="motd-dialog">
      <div class="motd-header">
        <h3>ChatBot MOTD</h3>
        <button type="button" class="motd-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="motd-body">
        <p class="motd-hint">
          Tenants see this on the ChatBot home screen until the end date/time.
          Markdown is supported (e.g. **bold**, *italic*, links).
        </p>

        <div v-if="loading" class="motd-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>

        <template v-else>
          <div class="motd-field-header">
            <label class="motd-field-label" for="motd-message">
              Message
              <span
                v-if="editingId && currentMotd?.expired"
                class="motd-expired-badge"
              >Expired</span>
              <span
                v-else-if="editingId"
                class="motd-active-badge"
              >Active</span>
            </label>
            <button
              v-if="editingId"
              type="button"
              class="motd-new-link"
              @click="startNewMessage"
            >
              Start new message
            </button>
          </div>
          <textarea
            id="motd-message"
            v-model="messageText"
            rows="6"
            maxlength="8000"
            placeholder="Message for tenants (text, emojis, markdown)..."
          ></textarea>

          <label class="motd-field-label" for="motd-expires"
            >End date &amp; time</label
          >
          <input
            id="motd-expires"
            v-model="expiresAtLocal"
            type="datetime-local"
          />

          <p v-if="errorText" class="motd-error">{{ errorText }}</p>
          <p v-if="successText" class="motd-success">{{ successText }}</p>
        </template>
      </div>

      <div class="motd-footer">
        <button type="button" class="motd-btn motd-btn-secondary" @click="$emit('close')">
          Cancel
        </button>
        <button
          type="button"
          class="motd-btn motd-btn-primary"
          :disabled="saving || loading"
          @click="saveMotd"
        >
          {{ saveButtonLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'ChatBotMotdDialog',
  emits: ['close'],
  data() {
    return {
      loading: true,
      saving: false,
      currentMotd: null,
      editingId: null,
      messageText: '',
      expiresAtLocal: '',
      errorText: '',
      successText: '',
    };
  },
  computed: {
    saveButtonLabel() {
      if (this.saving) return 'Saving...';
      return this.editingId ? 'Update MOTD' : 'Save MOTD';
    },
  },
  mounted() {
    this.loadMotd();
  },
  methods: {
    toDatetimeLocal(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    defaultExpiresLocal() {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      d.setMinutes(0, 0, 0);
      return this.toDatetimeLocal(d);
    },
    applyMotdToForm(motd) {
      if (motd) {
        this.editingId = motd.id;
        this.messageText = motd.messageText || '';
        this.expiresAtLocal = this.toDatetimeLocal(motd.expiresAt);
      } else {
        this.editingId = null;
        this.messageText = '';
        this.expiresAtLocal = this.defaultExpiresLocal();
      }
    },
    startNewMessage() {
      this.currentMotd = null;
      this.applyMotdToForm(null);
      this.errorText = '';
      this.successText = '';
    },
    async loadMotd() {
      this.loading = true;
      this.errorText = '';
      try {
        const response = await axios.get('/api/chatbot-motd');
        this.currentMotd = response.data?.motd || null;
        this.applyMotdToForm(this.currentMotd);
      } catch (error) {
        this.errorText =
          error.response?.data?.message || 'Failed to load current MOTD.';
      } finally {
        this.loading = false;
      }
    },
    async saveMotd() {
      this.errorText = '';
      this.successText = '';
      const text = this.messageText.trim();
      if (!text) {
        this.errorText = 'Enter a message before saving.';
        return;
      }
      if (!this.expiresAtLocal) {
        this.errorText = 'Choose an end date and time.';
        return;
      }

      const payload = {
        messageText: text,
        expiresAt: new Date(this.expiresAtLocal).toISOString(),
      };

      this.saving = true;
      const wasUpdate = !!this.editingId;
      try {
        const response = wasUpdate
          ? await axios.put(`/api/chatbot-motd/${this.editingId}`, payload)
          : await axios.post('/api/chatbot-motd', payload);

        this.currentMotd = response.data?.motd || null;
        this.applyMotdToForm(this.currentMotd);
        this.successText = wasUpdate ? 'MOTD updated.' : 'MOTD saved.';
      } catch (error) {
        this.errorText =
          error.response?.data?.message || 'Failed to save MOTD.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.motd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.motd-dialog {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #f8f8f8;
  border: 1px solid #888;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.motd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #5f4b8b;
  color: #fff;
}

.motd-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.motd-close {
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 1.1rem;
}

.motd-body {
  padding: 16px;
  overflow-y: auto;
}

.motd-hint {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.motd-loading {
  text-align: center;
  padding: 24px 0;
  color: #666;
}

.motd-field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0 6px;
}

.motd-field-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0;
}

.motd-new-link {
  border: none;
  background: transparent;
  color: #5f4b8b;
  cursor: pointer;
  font-size: 0.85rem;
  text-decoration: underline;
  padding: 0;
  white-space: nowrap;
}

.motd-expired-badge {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f8d7da;
  color: #842029;
  font-size: 0.75rem;
  font-weight: 600;
}

.motd-active-badge {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #d1e7dd;
  color: #0f5132;
  font-size: 0.75rem;
  font-weight: 600;
}

textarea,
input[type='datetime-local'] {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #bbb;
  border-radius: 4px;
  padding: 8px;
  font-size: 0.95rem;
}

textarea {
  resize: vertical;
  min-height: 120px;
}

.motd-error {
  color: #b00020;
  font-size: 0.85rem;
  margin: 10px 0 0;
}

.motd-success {
  color: #0f5132;
  font-size: 0.85rem;
  margin: 10px 0 0;
}

.motd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #ddd;
  background: #efefef;
}

.motd-btn {
  border: none;
  border-radius: 4px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.9rem;
}

.motd-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.motd-btn-secondary {
  background: #ddd;
  color: #333;
}

.motd-btn-primary {
  background: #5f4b8b;
  color: #fff;
}
</style>